/**
 * DataUsageList — compact adapter over `DataMappingViewerV2`.
 *
 * Sprint 17 — all content-plan surfaces render data-usage through the
 * strategy-blocks viewer so themes, topics, and posts share one look (chips,
 * entity summary, optional breadcrumb labels). This module keeps its old
 * `items[]` + `maxItems` + `testIdPrefix` prop signature so existing callers
 * (TopicDetailPanel, ThemeEditorDialog, ThemesTab, ResearchPackCard,
 * GenerationReviewDialog) don't have to change — they pass an array of items,
 * we wrap it into the `BlockDataUsage` shape V2 expects.
 *
 * When rendered inside a context that has HydratedSourceData available,
 * pass `resolveLabel` so each item card gets a breadcrumb ("Audience › Actor ›
 * Fear") above the monospace sourcePath. Otherwise the raw path is shown —
 * same as pre-Sprint-17 behaviour.
 */

import React from 'react';
import { Box } from '@mui/material';
import { DataMappingViewerV2 } from '@modules/strategy-blocks/components/DataMappingViewerV2';
import type { BlockDataUsage } from '@segmently/schema-models/simplified';

export interface DataUsageItemLike {
  sourcePath?: string;
  justification?: string;
  value?: unknown;
  category?: string;
  sourcePriority?: number;
  purposeId?: string;
}

export interface DataUsageListProps {
  items: DataUsageItemLike[];
  /** Optional label — defaults to "DATA USAGE". Pass '' to hide (renders without title). */
  label?: string;
  /** Max items to show (slice). Default 5. */
  maxItems?: number;
  /** Override testid root (for spec isolation per surface). */
  testIdPrefix?: string;
  /**
   * Optional resolver that turns a `sourcePath` into a human-readable
   * breadcrumb chain (e.g. "Frustrated Hardgainer › Maria › Fear: loses
   * motivation"). Callers that have HydratedSourceData in scope should build
   * this via `resolveSourcePath` from `@modules/content-plan/utils`.
   */
  resolveLabel?: (sourcePath: string) => string | null;
}

export const DataUsageList: React.FC<DataUsageListProps> = ({
  items,
  label = 'DATA USAGE',
  maxItems = 5,
  testIdPrefix = 'data-usage',
  resolveLabel,
}) => {
  if (!items || items.length === 0) return null;
  const sliced = items.slice(0, maxItems);
  // DataMappingViewerV2 consumes BlockDataUsage.items — coerce loose
  // DataUsageItemLike entries into the typed shape (optional fields passthrough).
  const dataUsage: BlockDataUsage = {
    items: sliced as BlockDataUsage['items'],
  };
  const title = label === '' ? '' : label;

  return (
    <Box data-testid={`${testIdPrefix}-list`}>
      <DataMappingViewerV2
        dataUsage={dataUsage}
        title={title || 'Data usage'}
        defaultExpanded
        resolveLabel={resolveLabel}
      />
    </Box>
  );
};

export default DataUsageList;
